import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MessageSquare, CheckCircle2, Clock, TrendingUp, ArrowRight, Zap, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [recentQuestions, setRecentQuestions] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState('all');
  const [credits, setCredits] = useState(null);
  const [dateFilter, setDateFilter] = useState('7'); // '1', '7', '14', '30', 'all'

  useEffect(() => {
    loadStores();
    loadCredits();
  }, []);

  useEffect(() => {
    if (stores.length > 0 || storeFilter === 'all') {
      loadDashboardData();
    }
  }, [storeFilter, dateFilter]);

  const loadStores = async () => {
    try {
      const response = await api.get('/settings/stores');
      setStores(response.data);
      loadDashboardData(); // Load data after stores are loaded
    } catch (error) {
      console.error('Stores load error:', error);
      loadDashboardData(); // Load anyway if no stores
    }
  };

  const loadCredits = async () => {
    try {
      const response = await api.get('/credits');
      setCredits(response.data);
    } catch (error) {
      console.error('Credits load error:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (storeFilter !== 'all') params.append('store_id', storeFilter);
      
      // Calculate date range based on filter
      if (dateFilter !== 'all') {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(dateFilter));
        
        console.log('🔥 FRONTEND - Date Filter:', dateFilter, 'days');
        console.log('🔥 FRONTEND - Start Date:', startDate.toISOString());
        console.log('🔥 FRONTEND - End Date:', endDate.toISOString());
        
        params.append('start_date', startDate.toISOString());
        params.append('end_date', endDate.toISOString());
      }
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      console.log('🔥 FRONTEND - API URL:', `/dashboard/stats${queryString}`);
      
      const [statsRes, trendRes, questionsRes] = await Promise.all([
        api.get(`/dashboard/stats${queryString}`),
        api.get('/dashboard/trend'),
        api.get(`/questions?status=all${storeFilter !== 'all' ? `&store_id=${storeFilter}` : ''}`),
      ]);

      console.log('🔥 FRONTEND - Stats Response:', statsRes.data);
      console.log('🔥 FRONTEND - Avg Response Time:', statsRes.data.avg_response_time_minutes, 'minutes');
      console.log('🔥 FRONTEND - Total vs Answered:', statsRes.data.total_questions, 'total,', statsRes.data.answered_questions, 'answered');

      setStats(statsRes.data);
      setTrend(trendRes.data);
      setRecentQuestions(questionsRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatResponseTime = (minutes) => {
    if (!minutes) return 'Veri yok';
    if (minutes < 60) return `${Math.round(minutes)} dakika`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours} saat ${mins} dk`;
  };

  const statCards = [
    {
      title: 'Toplam Sorular',
      value: stats?.total_questions || 0,
      icon: <MessageSquare className="h-5 w-5" />,
      color: 'bg-blue-50 text-blue-600',
    },
    {
      title: 'Cevaplanmış',
      value: stats?.answered_questions || 0,
      icon: <CheckCircle2 className="h-5 w-5" />,
      color: 'bg-green-50 text-green-600',
    },
    {
      title: 'Bekleyen',
      value: stats?.pending_questions || 0,
      icon: <Clock className="h-5 w-5" />,
      color: 'bg-orange-50 text-orange-600',
    },
    {
      title: 'Başarı Oranı',
      value: `${stats?.success_rate || 0}%`,
      icon: <TrendingUp className="h-5 w-5" />,
      color: 'bg-purple-50 text-purple-600',
    },
    {
      title: 'Ort. Cevap Süresi',
      value: formatResponseTime(stats?.avg_response_time_minutes),
      icon: <Zap className="h-5 w-5" />,
      color: 'bg-amber-50 text-amber-600',
    },
  ];

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-orange-50 text-orange-700 border-orange-200',
      sent: 'bg-green-50 text-green-700 border-green-200',
      failed: 'bg-red-50 text-red-700 border-red-200',
      approved: 'bg-blue-50 text-blue-700 border-blue-200',
      rejected: 'bg-slate-50 text-slate-700 border-slate-200',
    };

    const labels = {
      pending: 'Bekliyor',
      sent: 'Gönderildi',
      failed: 'Başarısız',
      approved: 'Onaylandı',
      rejected: 'Reddedildi',
    };

    return (
      <Badge className={variants[status] || variants.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Low Credit Warning */}
        {credits && credits.total_credits < 50 && (
          <Card className="mb-6 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-6 w-6 text-orange-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-orange-900 mb-1">Krediniz Azalıyor!</h3>
                    <p className="text-sm text-orange-700 mb-2">
                      Kalan kredi: <strong>{credits.total_credits}</strong> ({credits.questions_remaining} soru)
                    </p>
                    <p className="text-xs text-orange-600">
                      Krediniz bittiğinde AI cevaplar durur. Hemen kredi satın alın.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/pricing')}
                  className="bg-[#FF6000] hover:bg-orange-600"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Kredi Satın Al
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Dashboard</h1>
              <p className="text-slate-600">Mağazanızın performansını takip edin</p>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            {stores.length > 0 && (
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger className="w-64" data-testid="dashboard-store-select">
                  <SelectValue placeholder="Mağaza Seç" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Mağazalar</SelectItem>
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.store_name} ({store.marketplace === 'trendyol' ? 'Trendyol' : 'Hepsiburada'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-48" data-testid="dashboard-date-select">
                <SelectValue placeholder="Tarih Aralığı" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Son 1 Gün</SelectItem>
                <SelectItem value="7">Son 7 Gün</SelectItem>
                <SelectItem value="14">Son 14 Gün</SelectItem>
                <SelectItem value="30">Son 30 Gün</SelectItem>
                <SelectItem value="all">Tüm Zamanlar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            {[...Array(5)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            {statCards.map((stat, index) => (
              <Card key={index} className="border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold" style={{ fontFeatureSettings: "'tnum' 1" }}>
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat.color}`}>
                      {stat.icon}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Charts & Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Trend Chart */}
          <Card className="lg:col-span-8 border-slate-200">
            <CardHeader>
              <CardTitle>Son 7 Gün Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trend}>
                    <defs>
                      <linearGradient id="gradOrange" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF6000" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FF6000" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="questions"
                      stroke="#FF6000"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#gradOrange)"
                      name="Sorular"
                    />
                    <Area
                      type="monotone"
                      dataKey="answered"
                      stroke="#0EA5E9"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#gradBlue)"
                      name="Cevaplanan"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Recent Questions */}
          <Card className="lg:col-span-4 border-slate-200">
            <CardHeader>
              <CardTitle>Son Sorular</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : recentQuestions.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 text-sm">Henüz soru yok</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3 text-[#FF6000]"
                    onClick={() => navigate('/settings')}
                  >
                    Ayarları Yapılandır
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentQuestions.map((question) => (
                    <div
                      key={question.id}
                      className="p-3 border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition-all duration-200 cursor-pointer"
                      onClick={() => navigate('/questions')}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm font-medium text-slate-900 line-clamp-1">
                          {question.product.title}
                        </p>
                        {getStatusBadge(question.status)}
                      </div>
                      <p className="text-xs text-slate-600 line-clamp-2">{question.customer_question}</p>
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    className="w-full text-[#FF6000] hover:bg-orange-50"
                    onClick={() => navigate('/questions')}
                    data-testid="dashboard-view-all-questions"
                  >
                    Tümünü Görüntüle
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="mt-6 border-slate-200">
          <CardHeader>
            <CardTitle>Hızlı İşlemler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => navigate('/questions')}
                data-testid="dashboard-quick-questions"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-[#FF6000]" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Soruları İncele</p>
                    <p className="text-xs text-slate-600">Müşteri sorularını yönet</p>
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => navigate('/stores')}
                data-testid="dashboard-quick-stores"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Mağazalarım</p>
                    <p className="text-xs text-slate-600">Mağaza ekle ve yönet</p>
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => navigate('/credits')}
                data-testid="dashboard-quick-credits"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Kredi Yükle</p>
                    <p className="text-xs text-slate-600">Kredi satın al</p>
                  </div>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
